import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFormulaCatalog } from "../../../api/formulaEngineApi";
import { useI18nLanguage } from "../../../i18n/hooks/useI18nLanguage";

/**
 * CRE ƒ formula picker — catalog from GET /api/FormulaEngine/Formulas.
 */
function FormulaPickerPopover({
  open,
  onClose,
  onQueue,
  onManual,
  anchorRef,
}) {
  const { t } = useTranslation("common");
  const { language } = useI18nLanguage();
  const panelRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formulas, setFormulas] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    getFormulaCatalog()
      .then((list) => {
        if (!cancelled) setFormulas(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.message ||
              t("formulaEngine.loadFailed", {
                defaultValue: "Failed to load formulas.",
              }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (panel?.contains(e.target)) return;
      if (anchor?.contains?.(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const isHe = String(language || "").toLowerCase().startsWith("he");
  const q = query.trim().toLowerCase();
  const filtered = formulas.filter((f) => {
    if (!q) return true;
    const name = String(
      (isHe ? f.nameHe || f.nameEn : f.nameEn || f.nameHe) || f.formulaId || "",
    ).toLowerCase();
    const cat = String(f.category || "").toLowerCase();
    const id = String(f.formulaId || "").toLowerCase();
    return name.includes(q) || cat.includes(q) || id.includes(q);
  });

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full z-[80] mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-gray-300 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
      role="dialog"
      aria-label={t("formulaEngine.pickerTitle", {
        defaultValue: "Formula picker",
      })}
    >
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("formulaEngine.searchPlaceholder", {
          defaultValue: "Search formulas…",
        })}
        className="mb-2 w-full rounded-lg border border-gray-300 bg-gray-50 px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
      />
      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <p className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400">
            {t("loading", { defaultValue: "Loading…" })}
          </p>
        ) : error ? (
          <p className="px-2 py-3 text-xs text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400">
            {t("formulaEngine.empty", { defaultValue: "No formulas found." })}
          </p>
        ) : (
          filtered.map((formula) => {
            const label = isHe
              ? formula.nameHe || formula.nameEn || formula.formulaId
              : formula.nameEn || formula.nameHe || formula.formulaId;
            return (
              <div
                key={`${formula.formulaId}-${formula.version || ""}`}
                className="mb-1 flex items-stretch gap-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 px-2 py-2 text-left"
                  onClick={() => {
                    onQueue?.(formula);
                    onClose?.();
                  }}
                  title={t("formulaEngine.queueForProcessAi", {
                    defaultValue: "Queue for Process AI",
                  })}
                >
                  <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {label}
                    {formula.isEstimated ? (
                      <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                        ~
                      </span>
                    ) : null}
                  </div>
                  <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                    {[formula.category, formula.outputUnit, formula.version]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </button>
                <button
                  type="button"
                  className="shrink-0 px-2 text-xs font-semibold text-green-dark dark:text-[#CBEA7B]"
                  onClick={() => {
                    onManual?.(formula);
                    onClose?.();
                  }}
                  title={t("formulaEngine.calculateNow", {
                    defaultValue: "Calculate now",
                  })}
                >
                  {t("formulaEngine.manualShort", { defaultValue: "Manual" })}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default FormulaPickerPopover;
